'use client'

// SUMMARY: A single chat row — user bubble or assistant markdown with copy action and streaming indicator

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { Check, Copy, FileText, Image as ImageIcon, Loader2, Search, Sparkles, User } from 'lucide-react'
import { Logo } from '@/components/logo'
import { parseMessageContent } from '@/lib/ai/message-format'
import { stripChoiceResult } from '@/lib/ai/choice-form'
import { ChatMarkdown } from './chat-markdown'
import type { ChatMessage as ChatMessageType } from './use-chat'

/** User bubble: render attachments as chips and show only the typed text, not the raw context */
function UserContent({ content }: { content: string }) {
	const { docs, images, text } = parseMessageContent(content)
	// A choice card posts a hidden result block with its readable line - never show the machine block.
	const displayText = stripChoiceResult(text)
	const chips = [
		...images.map((img) => ({ name: img.name, image: true })),
		...docs.map((doc) => ({ name: doc.name, image: false })),
	]

	return (
		<div className="space-y-2">
			{chips.length > 0 && (
				<div className="flex flex-wrap gap-1.5">
					{chips.map((c, i) => (
						<span key={i} className="flex items-center gap-1 rounded-md bg-white/15 px-2 py-1 text-xs">
							{c.image ? <ImageIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
							<span className="max-w-[12rem] truncate">{c.name}</span>
						</span>
					))}
				</div>
			)}
			{displayText && <div className="whitespace-pre-wrap">{displayText}</div>}
		</div>
	)
}

export function ChatMessage({ message }: { message: ChatMessageType }) {
	const [copied, setCopied] = useState(false)
	const isUser = message.role === 'user'

	const copy = async () => {
		await navigator.clipboard.writeText(message.content)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	return (
		<div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
			<div
				className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
					isUser ? 'bg-blue-600 text-white' : 'bg-muted'
				}`}
			>
				{isUser ? <User className="h-4 w-4" /> : <Logo className="h-4 w-auto text-foreground" />}
			</div>

			<div
				className={`group flex min-w-0 flex-col gap-1 ${isUser ? 'max-w-[85%] items-end' : 'max-w-full items-start'}`}
			>
				<div
					className={`break-words rounded-2xl px-4 py-3 text-sm leading-relaxed ${
						isUser ? 'rounded-tr-sm bg-blue-600 text-white' : 'w-full rounded-tl-sm bg-muted text-foreground'
					}`}
				>
					{isUser ? (
						<UserContent content={message.content} />
					) : message.content ? (
						<ChatMarkdown content={message.content} />
					) : message.status ? (
						<StatusLine status={message.status} kind={message.statusKind} />
					) : message.streaming ? (
						<StreamingDots />
					) : null}
				</div>

				{!isUser && message.content && !message.streaming && (
					<button
						onClick={copy}
						className="-m-0.5 flex items-center gap-1 p-1.5 text-xs text-muted-foreground transition-opacity hover:text-foreground sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
					>
						{copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
					</button>
				)}
			</div>
		</div>
	)
}

function StatusLine({ status, kind }: { status: string; kind?: string }) {
	const t = useTranslations('dashboard.chat')
	// Document/project/research turns send a full localized progress sentence; web search sends a bare query.
	// 'thinking' is the immediate activity signal every turn opens with (replaced by any later real status).
	const isThinking = kind === 'thinking'
	const isSentence = isThinking || kind === 'document' || kind === 'project' || kind === 'research'
	const Icon = isThinking ? Sparkles : isSentence ? FileText : Search
	return (
		<span className="flex items-center gap-2 text-muted-foreground">
			<Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
			<Icon className="h-3.5 w-3.5 shrink-0" />
			<span className="truncate">
				{isSentence ? status || '…' : `${t('searching')}${status ? `: ${status}` : '…'}`}
			</span>
		</span>
	)
}

function StreamingDots() {
	return (
		<span className="flex items-center gap-1 py-0.5">
			<span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:0ms]" />
			<span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:150ms]" />
			<span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:300ms]" />
		</span>
	)
}
